import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Job, JobType, ParamSpec, ParamValue } from '../../services/JobMonitorService';
import { SymbolBank } from '../../components/common/SymbolBank';
import styles from '../JobsPage.module.css';

const isList = (spec: ParamSpec): boolean => spec.type === 'string_list' || spec.type === 'symbol_list';

const splitList = (value: string): string[] => value.split(',').map((item) => item.trim()).filter(Boolean);

interface ParamsEditorProps {
  job: Job;
  jobType: JobType | null;
  onSave: (params: Record<string, ParamValue>) => Promise<void>;
}

// What the job runs with today: its own saved value, or the type's default when
// it has never been set. An empty box means "use the job type's own default".
const currentValues = (job: Job, jobType: JobType | null): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const spec of jobType?.params ?? []) {
    const value = job.params[spec.name] ?? spec.default;
    values[spec.name] = value === null || value === undefined ? '' : renderValue(value);
  }
  return values;
};

const renderValue = (value: ParamValue): string =>
  Array.isArray(value) ? value.join(', ') : String(value);

const sameValues = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
};

// Blank fields are left out entirely so the server falls back to the type's default.
const toPayload = (specs: ParamSpec[], values: Record<string, string>): Record<string, ParamValue> => {
  const payload: Record<string, ParamValue> = {};
  for (const spec of specs) {
    const raw = (values[spec.name] ?? '').trim();
    if (spec.type === 'bool') {
      payload[spec.name] = raw === 'true';
      continue;
    }
    if (!raw) continue;
    if (spec.type === 'int') payload[spec.name] = Number(raw);
    else if (isList(spec)) payload[spec.name] = splitList(raw);
    else payload[spec.name] = raw;
  }
  return payload;
};

export const ParamsEditor: React.FC<ParamsEditorProps> = ({ job, jobType, onSave }) => {
  const serverValues = currentValues(job, jobType);
  const [draft, setDraft] = useState<Record<string, string>>(serverValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const lastSynced = useRef<Record<string, string>>(serverValues);

  // The jobs list is polled, so this prop is a new object on every tick; only
  // adopt it when a value actually changed on the server.
  useEffect(() => {
    if (sameValues(lastSynced.current, serverValues)) return;
    lastSynced.current = serverValues;
    setDraft(serverValues);
  }, [serverValues]);

  if (jobType === null) {
    return (
      <div>
        <h4 className={styles.sectionTitle}>Parameters</h4>
        <p className={styles.inlineNote}>This job's type is no longer in the approved catalogue.</p>
      </div>
    );
  }

  if (jobType.params.length === 0) {
    return (
      <div>
        <h4 className={styles.sectionTitle}>Parameters</h4>
        <p className={styles.inlineNote}>This job type takes no parameters.</p>
      </div>
    );
  }

  const set = (name: string, value: string) => {
    setSaved(false);
    setDraft((current) => ({ ...current, [name]: value }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(toPayload(jobType.params, draft));
      setSaved(true);
    } catch (e) {
      setError((e as Error).message || 'Could not save the parameters');
    } finally {
      setSaving(false);
    }
  };

  // A list with a known set of values is a pick-from-these, not free text.
  const toggle = (spec: ParamSpec, choice: string) => {
    const chosen = splitList(draft[spec.name] ?? '');
    const next = chosen.includes(choice) ? chosen.filter((item) => item !== choice) : [...chosen, choice];
    set(spec.name, spec.choices.filter((item) => next.includes(item)).join(', '));
  };

  const setSymbols = (spec: ParamSpec, symbols: string[]) => set(spec.name, symbols.join(', '));

  const field = (spec: ParamSpec) => {
    const id = `param-${job.job_id}-${spec.name}`;
    const value = draft[spec.name] ?? '';

    if (spec.type === 'symbol_list') {
      const symbols = splitList(value);
      return (
        <SymbolBank
          symbolBank={symbols}
          label={`${spec.name}${spec.required ? ' (required)' : ''}`}
          disabled={saving}
          onAddSymbol={(symbol) => setSymbols(spec, [...symbols, symbol])}
          onAddSymbols={(added) => setSymbols(spec, [...symbols, ...added])}
          onClearSymbols={() => setSymbols(spec, [])}
          onRemoveSymbol={(idx) => setSymbols(spec, symbols.filter((_, i) => i !== idx))}
        />
      );
    }

    if (isList(spec) && spec.choices.length > 0) {
      const chosen = splitList(value);
      return (
        <div className={styles.dayRow}>
          {spec.choices.map((choice) => (
            <button
              key={choice}
              type="button"
              disabled={saving}
              className={`${styles.dayToggle} ${chosen.includes(choice) ? styles.dayToggleActive : ''}`}
              onClick={() => toggle(spec, choice)}
            >
              {choice}
            </button>
          ))}
        </div>
      );
    }

    if (spec.type === 'bool') {
      return (
        <select id={id} className={styles.select} value={value || 'false'} disabled={saving} onChange={(e) => set(spec.name, e.target.value)}>
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      );
    }

    if (spec.choices.length > 0) {
      return (
        <select id={id} className={styles.select} value={value} disabled={saving} onChange={(e) => set(spec.name, e.target.value)}>
          {!spec.required && <option value="">— use the default —</option>}
          {spec.choices.map((choice) => (
            <option key={choice} value={choice}>{choice}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        id={id}
        className={styles.input}
        type={spec.type === 'int' ? 'number' : spec.type === 'date' ? 'date' : 'text'}
        value={value}
        min={spec.minimum ?? undefined}
        max={spec.maximum ?? undefined}
        disabled={saving}
        placeholder={isList(spec) ? 'comma separated' : ''}
        onChange={(e) => set(spec.name, e.target.value)}
      />
    );
  };

  return (
    <div>
      <h4 className={styles.sectionTitle}>Parameters</h4>
      {jobType.cost_warning && <p className={styles.warningNote}>{jobType.cost_warning}</p>}

      {jobType.params.map((spec) => (
        <div className={styles.field} key={spec.name}>
          {spec.type !== 'symbol_list' && (
            <label className={styles.label} htmlFor={`param-${job.job_id}-${spec.name}`}>
              {spec.name}{spec.required ? ' (required)' : ''}
            </label>
          )}
          {field(spec)}
          {spec.description && <span className={styles.paramHint}>{spec.description}</span>}
        </div>
      ))}

      {error && <p className={styles.errorBar}>{error}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.primaryBtn} onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save parameters'}
        </button>
      </div>

      {saved && <p className={styles.inlineNote}>Saved. The next run will use these values.</p>}
    </div>
  );
};
